// Messages: conversations with any trainer (server/social.ts pm, inbox), shown
// inside chat. Chat has one selection, shared by the chat panel beside the
// hunt and the full Chat page (#/chat): a channel (World, Region, Trade,
// Guild, Help), the list of conversations ('inbox'), message requests
// ('requests'), or one conversation. A first message from someone who isn't
// a friend or guildmate waits under Requests until you answer. Conversations
// themselves (F.threads) are shared with client/social.ts, which fetches them.
import { SOCIAL, DM_FROM, type DmFrom, type InboxRow, type PmDTO } from '../shared/social.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import { S } from './state.ts';
import { F, askThread, playerLink, THREAD_WAIT_MS } from './social.ts';
import { chatShown } from './chatfilter.ts';
import { trainerFace } from './outfits.ts';
import { esc, ago } from './fmt.ts';
import { route, go } from './router.ts';
import { confirmClick, isArmed } from './confirm.ts';

export const M = {
  inbox: null as InboxRow[] | null,
  /** The chat selection besides a channel: a conversation, or a list. Empty = the channel (client/hud.ts feedChannel). */
  dm: '',
  list: '' as '' | 'inbox' | 'requests',
};

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindMessages(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }
const op = (o: Extract<ClientMsg, { t: 'social' }>['op']) => send({ t: 'social', op: o });
const lc = (s: string) => s.toLowerCase();

const DM_FROM_NAME: Record<DmFrom, string> = { all: 'Everyone', kin: 'Friends and guild', friends: 'Friends only' };

export function loadInbox() { op({ k: 'inbox' }); }
export function onInbox(m: { rows: InboxRow[] }) { M.inbox = m.rows; rerender(); }

// ---------------- selection ----------------
export type ChatSel = { k: 'ch' } | { k: 'dm'; name: string } | { k: 'list'; which: 'inbox' | 'requests' };
export function chatSel(): ChatSel {
  if (M.list) return { k: 'list', which: M.list };
  if (M.dm) return { k: 'dm', name: M.dm };
  return { k: 'ch' };
}
export function selectChannel() { M.dm = ''; M.list = ''; }
export function selectDm(name: string) { M.dm = name; M.list = ''; askThread(name); loadInbox(); }
export function selectList(which: 'inbox' | 'requests') { M.list = which; M.dm = ''; loadInbox(); }

/** Whether the chat panel beside the hunt is on screen (it folds into a tab on narrow screens). */
const railShown = () => !S.tab && (document.getElementById('feed')?.offsetParent ?? null) !== null;
/** Whether a conversation is on screen, so a new message there counts as read. */
export function convShown(name: string): boolean {
  return lc(M.dm) === lc(name) && (S.page === 'chat' || railShown());
}

/** A message sent or received: keep the conversation list current. */
export function onDmNew() { if (M.inbox || M.list || M.dm || S.page === 'chat') loadInbox(); }

/** Open a conversation from a name: in the chat panel while it is on screen, else on the Chat page. */
export function openConversation(name: string) {
  selectDm(name);
  if (S.page === 'chat' || !railShown()) go(`#/chat/dm/${encodeURIComponent(name)}`);
  rerender();
}

/** "/w name text" (or /msg, /dm, /whisper) typed in chat: the text goes as a message and the conversation opens. */
export function chatCommand(text: string): boolean {
  const m = text.match(/^\/(?:w|msg|dm|whisper)\s+(\S+)(?:\s+([\s\S]*))?$/i);
  if (!m) return false;
  const [, name, body] = m;
  if (body?.trim()) op({ k: 'pm', name, text: body });
  selectDm(name);
  if (S.page === 'chat') go(`#/chat/dm/${encodeURIComponent(name)}`);
  return true;
}

/** Send what was typed in the chat box while a conversation is selected. */
export function sendDm(name: string, text: string) { if (!chatCommand(text)) op({ k: 'pm', name, text }); }

// ---------------- pieces ----------------
const dot = (on: boolean) => `<i class="gdot${on ? '' : ' off'}" title="${on ? 'Online' : 'Offline'}"></i>`;
export const inboxRow = (name: string) => M.inbox?.find((x) => lc(x.name) === lc(name));

/** One conversation in a list. href: where a click goes (the panel uses data-act instead). */
export function convRowHtml(r: InboxRow, sel: string, page: boolean) {
  const me = S.trainer?.name ?? '';
  const mine = lc(r.last.from) === lc(me);
  const tag = page ? 'a' : 'button';
  const attrs = page ? `href="#/chat/dm/${encodeURIComponent(r.name)}"` : `type="button" data-act="dm-sel" data-id="${esc(r.name)}"`;
  return `<${tag} class="cv-row${lc(sel) === lc(r.name) ? ' on' : ''}${r.unread ? ' new' : ''}" ${attrs} data-key="r-${esc(lc(r.name))}">
    <span class="cv-face">${trainerFace(r.outfit)}</span><span class="cv-mid"><span class="cv-nm">${dot(r.online)}${r.tag ? `<span class="gtag">[${esc(r.tag)}]</span>` : ''}<b>${esc(r.name)}</b></span>
    <span class="cv-pv">${mine ? 'You: ' : ''}${esc(chatShown(r.last.text))}</span></span>
    <span class="cv-meta"><span class="muted">${ago(r.last.ts)}</span>${r.unread ? `<span class="unread">${r.unread}</span>` : ''}</span></${tag}>`;
}

/** The conversation list (or the requests) as rows; for the chat panel. */
export function listHtml(which: 'inbox' | 'requests', sel = '', page = false) {
  const rows = M.inbox;
  if (!rows) return '<p class="hint" data-key="wait">Loading…</p>';
  const reqs = rows.filter((x) => x.request);
  const list = rows.filter((x) => x.request === (which === 'requests'));
  const head = which === 'requests'
    ? (page ? '<a class="cv-sub" href="#/chat/inbox" data-key="back">‹ Messages</a>' : '<button type="button" class="cv-sub" data-act="dm-list" data-id="inbox" data-key="back">‹ Messages</button>')
    : reqs.length ? (page ? `<a class="cv-sub" href="#/chat/requests" data-key="reqs">Requests <span class="unread">${reqs.length}</span></a>` : `<button type="button" class="cv-sub" data-act="dm-list" data-id="requests" data-key="reqs">Requests <span class="unread">${reqs.length}</span></button>`) : '';
  return `${head}${list.map((x) => convRowHtml(x, sel, page)).join('') || `<p class="hint" data-key="none">${which === 'requests' ? 'No requests.' : 'No conversations yet.'}</p>`}`;
}

const dayOf = (ts: number) => new Date(ts).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
const timeOf = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** A conversation's lines with day breaks; a retry instead of an endless Loading. */
export function convLinesHtml(name: string) {
  const msgs = F.threads.get(lc(name));
  if (!msgs) {
    const at = F.asked.get(lc(name)) ?? 0;
    if (S.online && Date.now() - at < THREAD_WAIT_MS) return '<p class="hint" data-key="wait">Loading…</p>';
    return `<p class="hint" data-key="retry">${S.online ? 'Didn\'t load.' : 'Offline.'} <button class="btn tiny" data-act="fr-thread-retry" data-id="${esc(name)}">Try again</button></p>`;
  }
  const me = S.trainer?.name ?? '';
  let day = '';
  const out: string[] = [];
  const row = inboxRow(name);
  if (row?.request) out.push(`<div class="cv-req" data-key="req"><span class="pill">Message request</span><button class="btn tiny" data-act="dm-hide" data-id="${esc(name)}">Delete</button><button class="btn tiny danger-outline" data-act="dm-block" data-id="${esc(name)}">Block</button></div>`);
  for (const m of msgs as PmDTO[]) {
    const d = dayOf(m.ts);
    if (d !== day) { day = d; out.push(`<div class="cv-day" data-key="d${esc(d)}">${esc(d)}</div>`); }
    out.push(`<div class="cv-m ${lc(m.from) === lc(me) ? 'me' : 'them'}" data-key="p${m.id}"><span>${esc(chatShown(m.text))}</span><time>${timeOf(m.ts)}</time></div>`);
  }
  if (!msgs.length) out.push(`<p class="hint" data-key="none">Say hi to ${esc(name)}.</p>`);
  return out.join('');
}

/** The bar over a conversation: back to the list, who, and (on the page) Report and Block. */
export function convHeadHtml(name: string, page: boolean) {
  const r = inboxRow(name), n = esc(name);
  const blocked = F.data?.blocked.some((b) => b.kind === 'block' && lc(b.name) === lc(name));
  const back = page ? `<a class="btn tiny cv-back" href="#/chat${r?.request ? '/requests' : ''}" aria-label="Back to messages">‹</a>` : `<button class="btn tiny cv-back" data-act="dm-list" data-id="${r?.request ? 'requests' : 'inbox'}" aria-label="Back to messages">‹</button>`;
  const acts = page
    ? `<span class="cv-acts"><button class="btn tiny" data-act="dm-report" data-id="${n}">${isArmed(`dmreport:${name}`) ? 'Confirm report' : 'Report'}</button><button class="btn tiny ${isArmed(`dmblock:${name}`) ? 'danger' : 'danger-outline'}" data-act="dm-block" data-id="${n}">${blocked ? 'Unblock' : isArmed(`dmblock:${name}`) ? 'Confirm block' : 'Block'}</button></span>`
    : `<a class="cv-x" href="#/chat/dm/${encodeURIComponent(name)}" title="Open in Chat" aria-label="Open in Chat">⤢</a>`;
  return `<div class="cv-head">${back}${r ? dot(r.online) : ''}${playerLink(name, `<b>${n}</b>`)}${r ? `<span class="muted">Lv ${r.level}</span>` : ''}${acts}</div>`;
}

export const isBlocked = (name: string) => !!F.data?.blocked.some((b) => b.kind === 'block' && lc(b.name) === lc(name));

export function dmFromSelect() {
  const from = F.data?.dmFrom ?? 'all';
  return `<select data-dm="from" aria-label="Who can message you">${DM_FROM.map((v) => `<option value="${v}"${v === from ? ' selected' : ''}>${DM_FROM_NAME[v]}</option>`).join('')}</select>`;
}

// ---------------- events ----------------
export function messagesClick(el: HTMLElement): boolean {
  const act = el.dataset.act ?? '';
  if (!act.startsWith('dm-')) return false;
  const id = el.dataset.id ?? '';
  switch (act) {
    case 'dm-open': openConversation(id); return true;
    case 'dm-sel': selectDm(id); break;
    case 'dm-list': selectList(id === 'requests' ? 'requests' : 'inbox'); break;
    case 'dm-hide': op({ k: 'pmHide', name: id }); selectList('requests'); if (S.page === 'chat') go('#/chat/requests'); break;
    case 'dm-report': if (confirmClick(`dmreport:${id}`)) op({ k: 'pmReport', name: id }); break;
    case 'dm-block': {
      if (isBlocked(id)) op({ k: 'block', name: id, kind: 'block', on: false });
      else if (confirmClick(`dmblock:${id}`)) { op({ k: 'block', name: id, kind: 'block', on: true }); selectList('inbox'); if (S.page === 'chat') go('#/chat/inbox'); }
      break;
    }
    default: return false;
  }
  rerender();
  return true;
}

export function messagesInput(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.dm === 'from') {
    const v = el.value as DmFrom;
    if (DM_FROM.includes(v)) { op({ k: 'dmFrom', v }); if (F.data) F.data.dmFrom = v; }
    return true;
  }
  return false;
}

/** Unread messages and waiting requests, for the app bar and the chat tabs. */
export function dmWaiting(): { unread: number; requests: number } {
  return { unread: F.data?.dmUnread ?? 0, requests: F.data?.dmRequests ?? 0 };
}

/** The Chat page route: #/chat, #/chat/:channel, #/chat/inbox, #/chat/requests, #/chat/dm/:name. */
export function chatRouteSel(): { ch?: string; dm?: string; list?: 'inbox' | 'requests' } {
  const r = route();
  if (r.sub === 'dm' && r.sub2) return { dm: r.sub2 };
  if (r.sub === 'inbox' || r.sub === 'requests') return { list: r.sub };
  return r.sub ? { ch: r.sub } : {};
}

/** Reports for the Admin page. */
export function reportsAdminSection(reports: { id: number; ts: number; reporter: string; target: string; lines: PmDTO[] }[] | undefined) {
  if (!reports?.length) return '';
  return `<h3 style="margin-top:16px">Message reports</h3>
    ${reports.map((r) => `<details class="panel dm-report"><summary>${new Date(r.ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} · ${esc(r.reporter)} reported <b>${esc(r.target)}</b> · ${ago(r.ts)}</summary>
      <ol class="dm-lines">${r.lines.map((m) => `<li><b>${esc(m.from)}:</b> ${esc(m.text)}</li>`).join('')}</ol></details>`).join('')}`;
}

export { SOCIAL };
